import React, { useState, useCallback, Suspense, lazy } from 'react';
import { useNavigate } from '@/lib/router-compat';
import { Search, ChevronDown, Car, ShieldCheck, Percent } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useMakeModel } from '@/hooks/useMakeModel';
import { usePlatformStats } from '@/hooks/usePlatformStats';
import { useLang, type Lang } from '@/i18n/useLang';
import heroCar from '@/assets/hero-car-bright.jpg';
import { useCountry } from '@/hooks/useCountry';
import { useBuyerCategoryCounts } from '@/hooks/useBuyerCategoryCounts';
import { BUYER_CATEGORY_CODES, BUYER_SEARCH_TYPES, hasPositiveBuyerCount } from '@/utils/buyerCategoryCounts';
import { trackHeroSearch } from '@/utils/buyerJourneyTracking';
import roBuyer from '@/data/translations/ro/buyer.json';
import enBuyer from '@/data/translations/en/buyer.json';
import deBuyer from '@/data/translations/de/buyer.json';

// Lazy-load non-critical interactive pieces so the LCP frame paints sooner.
const IntentChips = lazy(() =>
  import('./IntentChips').then((m) => ({ default: m.IntentChips }))
);
const HeroSearchAutocomplete = lazy(() => import('./HeroSearchAutocomplete'));
const AdvancedFiltersSheet = lazy(() =>
  import('@/components/vehicles/AdvancedFiltersSheet').then((m) => ({ default: m.AdvancedFiltersSheet }))
);
const NLSearchBar = lazy(() =>
  import('@/components/vehicles/NLSearchBar').then((m) => ({ default: m.NLSearchBar }))
);

type TabKey = 'used' | 'truck' | 'motorcycle';

const COPY: Record<Lang, {
  heading: string;
  subBefore: string;
  subAfter: string;
  fallbackQty: string;
  tabs: Record<TabKey, { label: string; short: string }>;
  brand: string; allBrands: string;
  model: string; allModels: string;
  maxPrice: string; anyPrice: string;
  city: string; wholeCountry: string;
  search: string;
  intent: string;
  newBadge: string;
  imgAlt: string;
  statVehicles: string; statDealers: string; statCities: string; statNoCommission: string;
  priceUpTo: (n: string, cur: string) => string;
  priceCurrency: string;
  citiesAll: Record<string, string>;
}> = {
  ro: {
    heading: 'Mașini second hand și noi în România, de la dealeri verificați',
    subBefore: '', subAfter: 'de vehicule active',
    fallbackQty: 'Vehicule',
    tabs: {
      used: { label: 'Mașini', short: 'Mașini' },
      truck: { label: roBuyer.tabs.truck, short: roBuyer.tabs.truck },
      motorcycle: { label: 'Motociclete', short: 'Moto' },
    },
    brand: 'Marcă', allBrands: 'Toate mărcile',
    model: 'Model', allModels: 'Toate modelele',
    maxPrice: 'Preț maxim', anyPrice: 'Orice preț',
    city: 'Oraș', wholeCountry: 'Toată țara',
    search: 'Caută',
    intent: 'Sau spune-ne ce cauți:',
    newBadge: 'NOU',
    imgAlt: 'Mașini de vânzare în România',
    statVehicles: 'Vehicule active', statDealers: 'Dealeri verificați', statCities: 'Orașe majore', statNoCommission: 'Comision vânzare',
    priceUpTo: (n, cur) => `până la ${n} ${cur}`, priceCurrency: '€',
    citiesAll: { '': 'Toată țara', bucuresti: 'București', 'cluj-napoca': 'Cluj-Napoca', timisoara: 'Timișoara', iasi: 'Iași', constanta: 'Constanța', brasov: 'Brașov', craiova: 'Craiova', sibiu: 'Sibiu' },
  },
  en: {
    heading: 'RO Auto Market: Used cars from verified dealers',
    subBefore: '', subAfter: 'active vehicles',
    fallbackQty: 'Vehicles',
    tabs: {
      used: { label: 'Used cars', short: 'Used' },
      truck: { label: enBuyer.tabs.truck, short: enBuyer.tabs.truck },
      motorcycle: { label: 'Motorcycles', short: 'Moto' },
    },
    brand: 'Make', allBrands: 'All makes',
    model: 'Model', allModels: 'All models',
    maxPrice: 'Max price', anyPrice: 'Any price',
    city: 'City', wholeCountry: 'Whole country',
    search: 'Search',
    intent: 'Or tell us what you’re looking for:',
    newBadge: 'NEW',
    imgAlt: 'Cars for sale in the UK',
    statVehicles: 'Active vehicles', statDealers: 'Verified dealers', statCities: 'Major cities', statNoCommission: 'Sales commission',
    priceUpTo: (n, cur) => `up to ${cur}${n}`, priceCurrency: '£',
    citiesAll: { '': 'Whole country', london: 'London', manchester: 'Manchester', birmingham: 'Birmingham', leeds: 'Leeds', glasgow: 'Glasgow', liverpool: 'Liverpool', bristol: 'Bristol', edinburgh: 'Edinburgh' },
  },
  de: {
    heading: 'RO Auto Market: Gebrauchtwagen von geprüften Händlern',
    subBefore: '', subAfter: 'aktive Fahrzeuge',
    fallbackQty: 'Fahrzeuge',
    tabs: {
      used: { label: 'Gebrauchtwagen', short: 'Gebraucht' },
      truck: { label: deBuyer.tabs.truck, short: deBuyer.tabs.truck },
      motorcycle: { label: 'Motorräder', short: 'Moto' },
    },
    brand: 'Marke', allBrands: 'Alle Marken',
    model: 'Modell', allModels: 'Alle Modelle',
    maxPrice: 'Höchstpreis', anyPrice: 'Beliebig',
    city: 'Stadt', wholeCountry: 'Ganzes Land',
    search: 'Suchen',
    intent: 'Oder beschreiben Sie, was Sie suchen:',
    newBadge: 'NEU',
    imgAlt: 'Autos zum Verkauf in Deutschland',
    statVehicles: 'Aktive Fahrzeuge', statDealers: 'Geprüfte Händler', statCities: 'Großstädte', statNoCommission: 'Verkaufsprovision',
    priceUpTo: (n, cur) => `bis ${n} ${cur}`, priceCurrency: '€',
    citiesAll: { '': 'Ganzes Land', berlin: 'Berlin', hamburg: 'Hamburg', muenchen: 'München', koeln: 'Köln', frankfurt: 'Frankfurt', stuttgart: 'Stuttgart', duesseldorf: 'Düsseldorf', leipzig: 'Leipzig' },
  },
};

const PRICE_VALUES = ['', '5000', '10000', '15000', '20000', '30000', '50000', '100000'];

const HeroV3: React.FC = () => {
  const navigate = useNavigate();
  const lang = useLang();
  const t = COPY[lang];
  const buyer = { ro: roBuyer, en: enBuyer, de: deBuyer }[lang];
  const { country } = useCountry();
  const { data: categoryCounts } = useBuyerCategoryCounts(country);
  const locale = lang === 'de' ? 'de-DE' : lang === 'en' ? 'en-GB' : 'ro-RO';
  const { data: platformStats } = usePlatformStats();
  const [tab, setTab] = useState<TabKey>('used');
  const selectedCount = categoryCounts?.[tab];
  const vehicleTypeForTab = BUYER_SEARCH_TYPES[tab];
  const { makes, getModelsForMake, loading } = useMakeModel(vehicleTypeForTab);

  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [city, setCity] = useState('');
  const [showMoreModes, setShowMoreModes] = useState(false);

  React.useEffect(() => {
    setMake('');
    setModel('');
  }, [tab]);

  const models = make ? getModelsForMake(make) : [];

  const onSearch = useCallback(() => {
    trackHeroSearch(tab);
    const p = new URLSearchParams();
    if (tab === 'used') p.set('condition', 'used');
    if (tab === 'truck') p.set('type', BUYER_SEARCH_TYPES.truck);
    if (tab === 'motorcycle') p.set('type', 'motorcycle');
    if (make) p.set('make', make);
    if (model) p.set('model', model);
    if (priceMax && priceMax !== 'any') p.set('priceMax', priceMax);
    if (city && city !== 'any') p.set('city', city);
    navigate(`/vehicles?${p.toString()}`);
  }, [tab, make, model, priceMax, city, navigate]);

  const tabs: { key: TabKey }[] = [{ key: 'used' }, { key: 'truck' }, { key: 'motorcycle' }];

  const activeVehicles = platformStats?.activeListings ?? 0;

  const subtitle = `${hasPositiveBuyerCount(activeVehicles) ? `${buyer.hero_active.replace('{count}', activeVehicles.toLocaleString(locale))} ` : ''}${buyer.hero_direct}`;
  const verifiedDealers = platformStats?.verifiedDealers;

  const cityEntries = Object.entries(t.citiesAll);

  const moreLabel = lang === 'de'
    ? 'Andere Suchoptionen (KI, Freitext)'
    : lang === 'en'
      ? 'Other ways to search (AI, free text)'
      : 'Alte moduri de căutare (AI, text liber)';

  const renderSearchCard = (idp: string) => (
    <div className="rounded bg-card text-foreground shadow-card overflow-hidden border border-border">


      <div className="flex border-b border-border">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`flex-1 px-2 md:px-5 py-3 md:py-4 text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
              tab === tb.key
                ? 'text-primary border-b-2 border-primary -mb-px'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="md:hidden">{t.tabs[tb.key].short}</span>
            <span className="hidden md:inline">{t.tabs[tb.key].label}</span>
          </button>
        ))}
      </div>

      <div className="p-4 md:p-5">
        {/* Native GET form — works even before JS hydration finishes on slow mobile.
            Only hidden inputs live inside it; the Radix selects stay outside so no
            unnamed form controls are generated. */}
        <form
          id={`hero-search-${idp}`}
          method="get"
          action="/vehicles"
          className="hidden"
          onSubmit={(e) => { e.preventDefault(); onSearch(); }}
        >
          {tab === 'used' && <input type="hidden" name="condition" value="used" />}
          {tab === 'truck' && <input type="hidden" name="type" value={BUYER_SEARCH_TYPES.truck} />}
          {tab === 'motorcycle' && <input type="hidden" name="type" value="motorcycle" />}
          {make && <input type="hidden" name="make" value={make} />}
          {model && <input type="hidden" name="model" value={model} />}
          {priceMax && priceMax !== 'any' && <input type="hidden" name="priceMax" value={priceMax} />}
          {city && city !== 'any' && <input type="hidden" name="city" value={city} />}
        </form>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-1">
            <label htmlFor={`hero-make-${idp}`} className="text-xs font-medium text-muted-foreground mb-1 block">{t.brand}</label>

            <Select name={`hero-make-${idp}`} value={make} onValueChange={(v) => { setMake(v); setModel(''); }} disabled={loading}>
              <SelectTrigger id={`hero-make-${idp}`} className="h-11 text-foreground [&>span]:text-foreground" aria-label={t.brand}><SelectValue placeholder={t.allBrands} /></SelectTrigger>
              <SelectContent className="z-50 bg-popover max-h-72">
                {makes.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <label htmlFor={`hero-model-${idp}`} className="text-xs font-medium text-muted-foreground mb-1 block">{t.model}</label>
            <Select name={`hero-model-${idp}`} value={model} onValueChange={setModel} disabled={!make}>
              <SelectTrigger id={`hero-model-${idp}`} className="h-11 text-foreground [&>span]:text-foreground" aria-label={t.model}><SelectValue placeholder={t.allModels} /></SelectTrigger>

              <SelectContent className="z-50 bg-popover max-h-72">
                {models.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <label htmlFor={`hero-price-${idp}`} className="text-xs font-medium text-muted-foreground mb-1 block">{t.maxPrice}</label>
            <Select name={`hero-price-${idp}`} value={priceMax} onValueChange={setPriceMax}>
              <SelectTrigger id={`hero-price-${idp}`} className="h-11 text-foreground [&>span]:text-foreground" aria-label={t.maxPrice}><SelectValue placeholder={t.anyPrice} /></SelectTrigger>
              <SelectContent className="z-50 bg-popover">

                {PRICE_VALUES.map((v) => (
                  <SelectItem key={v || 'any'} value={v || 'any'}>
                    {v ? t.priceUpTo(Number(v).toLocaleString(locale), t.priceCurrency) : t.anyPrice}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <label htmlFor={`hero-city-${idp}`} className="text-xs font-medium text-muted-foreground mb-1 block">{t.city}</label>
            <Select name={`hero-city-${idp}`} value={city} onValueChange={setCity}>
              <SelectTrigger id={`hero-city-${idp}`} className="h-11 text-foreground [&>span]:text-foreground" aria-label={t.city}><SelectValue placeholder={t.wholeCountry} /></SelectTrigger>

              <SelectContent className="z-50 bg-popover max-h-72">
                {cityEntries.map(([v, label]) => (
                  <SelectItem key={v || 'any'} value={v || 'any'}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Advanced filters trigger — full-width row between Oraș and CAUTĂ */}
        <div className="mt-3">
          <Suspense fallback={<div className="h-11 rounded-md bg-muted/40 animate-pulse" />}>
            <AdvancedFiltersSheet
              categoryCode={BUYER_CATEGORY_CODES[tab][0]}
              initialFilters={{
                make: make || undefined,
                model: model || undefined,
                priceMax: priceMax && priceMax !== 'any' ? Number(priceMax) : undefined,
                locationCity: city && city !== 'any' ? city : undefined,
              }}
            />

          </Suspense>
        </div>

        {/* CAUTĂ — inverted cream CTA, editorial (Bring a Trailer style) */}
        <div className="mt-3">
          <Button
            type="submit"
            form={`hero-search-${idp}`}
            className="w-full h-11 touch-manipulation bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm rounded-xs shadow-none border border-primary"
          >
            <Search className="h-4 w-4 mr-2" />
            {hasPositiveBuyerCount(selectedCount) ? buyer.search_count.replace('{count}', selectedCount.toLocaleString(locale)).replace('{noun}', buyer.nouns[tab]) : t.search}
          </Button>
          <p className="mt-2 text-xs text-muted-foreground text-center">{buyer.search_reassurance}</p>
        </div>


        {/* Secondary search modes — collapsed by default (Hick's Law) */}
        <div className="mt-4 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => setShowMoreModes((v) => !v)}
            aria-expanded={showMoreModes}
            className="flex w-full min-h-[44px] items-center justify-between gap-2 text-sm font-medium text-muted-foreground transition-colors"
          >
            <span className="flex items-center gap-2">
              {moreLabel}
              <Badge variant="secondary" className="text-[10px] bg-accent/10 text-accent border-accent/20">{t.newBadge}</Badge>
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${showMoreModes ? 'rotate-180' : ''}`} />
          </button>

          {showMoreModes && (
            <div className="mt-4 space-y-5">
              <Suspense fallback={<div className="h-11 rounded-md bg-muted/40 animate-pulse" />}>
                <HeroSearchAutocomplete />
              </Suspense>

              <Suspense fallback={<div className="h-11 rounded-md bg-muted/40 animate-pulse" />}>
                <NLSearchBar />
              </Suspense>

              <div className="pt-4 border-t border-border">
                <div className="text-sm font-medium text-muted-foreground mb-3">{t.intent}</div>
                <Suspense fallback={<div className="h-8" />}>
                  <IntentChips />
                </Suspense>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );

  return (
    <section className="bg-background text-foreground">
      <div className="relative w-full">
        <picture>
          <source
            type="image/avif"
            srcSet="/images/hero-car-bright-768.avif 768w, /images/hero-car-bright-1536.avif 1536w"
            sizes="(max-width: 767px) 100vw, 1536px"
          />
          <source
            type="image/webp"
            srcSet="/images/hero-car-bright-768.webp 768w, /images/hero-car-bright-1536.webp 1536w"
            sizes="(max-width: 767px) 100vw, 1536px"
          />
          <img
            src={heroCar}
            alt={t.imgAlt}
            width={1920}
            height={1080}
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            className="w-full h-[300px] md:h-[460px] object-cover object-[center_70%]"
          />
        </picture>

        <div aria-hidden="true" className="absolute inset-0 pointer-events-none hero-photo-overlay" />
        <div className="absolute inset-x-0 top-0 p-5 lg:p-8 max-w-3xl">
          <h1 className="font-serif text-foreground text-3xl md:text-5xl lg:text-6xl leading-tight">{t.heading}</h1>
          <p className="mt-3 min-h-12 text-sm md:text-base text-foreground" aria-live="polite">{subtitle}</p>
        </div>
      </div>
      <div className="container mx-auto px-4 -mt-5 md:-mt-16 relative z-10">
        <div className="mx-auto w-full max-w-5xl">{renderSearchCard('search')}</div>
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-0 py-3 md:grid-cols-3 md:gap-4" aria-live="polite">
          <div className="flex min-h-10 items-center justify-center gap-2 text-sm text-foreground">
            {hasPositiveBuyerCount(activeVehicles) && <><Car className="h-4 w-4 shrink-0 text-primary" /><span>{buyer.trust_active.replace('{count}', activeVehicles.toLocaleString(locale))}</span></>}
          </div>
          <div className="flex min-h-10 items-center justify-center gap-2 text-sm text-foreground">
            {hasPositiveBuyerCount(verifiedDealers) && <><ShieldCheck className="h-4 w-4 shrink-0 text-primary" /><span>{buyer.trust_dealers.replace('{count}', verifiedDealers.toLocaleString(locale))}</span></>}
          </div>
          <div className="flex min-h-10 items-center justify-center gap-2 text-sm text-foreground"><Percent className="h-4 w-4 shrink-0 text-primary" /><span>{buyer.trust_commission}</span></div>
        </div>
      </div>
    </section>
  );
};

export default HeroV3;
